import type { Patient, Recipient } from "src/types";
import { fold, foldExpanded } from "src/helpers/textFold";

export const formatBirthDate = (iso?: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" });
};

export const patientName = (p: { firstName?: string; lastName?: string; title?: string }) =>
    [p.title, p.firstName, p.lastName].filter(Boolean).join(" ");

/** Words that a query token may match the start of — name parts, plus the birth date. */
function searchWords(p: Patient): string[] {
    const name = [p.firstName, p.lastName].filter(Boolean).join(" ");
    const words = [...fold(name).split(/[\s-]+/), ...foldExpanded(name).split(/[\s-]+/)];
    return words.filter(Boolean);
}

/**
 * Patients matching every word of the query: a name word must start a first/last name part;
 * a word with digits must appear in the birth date (dd.mm.yyyy). Sorted by last, then first name.
 */
export function searchPatients(patients: Patient[], query: string, limit = 8): Patient[] {
    const tokens = query.trim().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return [];
    const matches = patients.filter((p) => {
        const words = searchWords(p);
        const birth = formatBirthDate(p.birthDate);
        return tokens.every((token) =>
            /\d/.test(token)
                ? birth.includes(token)
                : words.some((w) => w.startsWith(fold(token)) || w.startsWith(foldExpanded(token))),
        );
    });
    return matches
        .sort((a, b) => (a.lastName ?? "").localeCompare(b.lastName ?? "", "de") || (a.firstName ?? "").localeCompare(b.firstName ?? "", "de"))
        .slice(0, limit);
}

/** Splits a typed query into a name prefill for "new patient" — "Anna Schmidt" → first + last. */
export function nameFromQuery(query: string): { firstName?: string; lastName?: string } {
    const words = query.trim().split(/\s+/).filter((w) => w && !/\d/.test(w));
    if (words.length === 0) return {};
    if (words.length === 1) return { lastName: words[0] };
    return { firstName: words.slice(0, -1).join(" "), lastName: words[words.length - 1] };
}

export type DigitalConsentState = "signed" | "requested" | "missing" | "postOnly";
export type PostConsentState = "signed" | "covered" | "missing";

/** Does the recipient's valid consent on this channel name the patient? Every patient needs their
 *  own consent — a child added after the parent signed isn't covered (backend consentStatus.ts). */
export function consentNamesPatient(recipient: Recipient, channel: "digital" | "post", patientId?: string): boolean {
    const consent = channel === "digital" ? recipient.consentDigital : recipient.consentPost;
    return !!consent?.valid && !(patientId && consent.uncoveredPatientIds?.includes(patientId));
}

/** The recipient holds a valid consent, but it doesn't name this patient (a newborn, a sibling). */
export function consentMissesPatient(recipient: Recipient, patientId?: string): boolean {
    if (!patientId) return false;
    return (!!recipient.consentDigital?.valid && !consentNamesPatient(recipient, "digital", patientId)) ||
        (!!recipient.consentPost?.valid && !consentNamesPatient(recipient, "post", patientId));
}

/** Consent per channel, as the recipient page shows it (consentDigital.valid already includes a
 *  submitted questionnaire), for `patientId` when given. A post-only recipient can't consent
 *  digitally at all. Digital consent also allows sending by post (invoiceController
 *  `postConsentAccepted`) — post shows as "covered". */
export function consentStates(recipient: Recipient, patientId?: string): { digital: DigitalConsentState; post: PostConsentState } {
    const requested = recipient.questionnaireStatus === "sent" || recipient.questionnaireStatus === "opened";
    const digital: DigitalConsentState = recipient.postOnly
        ? "postOnly"
        : consentNamesPatient(recipient, "digital", patientId)
          ? "signed"
          : requested
            ? "requested"
            : "missing";
    const post: PostConsentState = consentNamesPatient(recipient, "post", patientId) ? "signed" : digital === "signed" ? "covered" : "missing";
    return { digital, post };
}

/** What step 1 still has to ask for (story 11). Digital consent also covers sending by post
 *  (invoiceController `postConsentAccepted`), so it's what we ask for — except for a post-only
 *  recipient, who can only consent to post, on paper or by QR. */
export type ConsentNeed =
    | { kind: "none" }
    | { kind: "digital"; requested: boolean; postSigned: boolean; notNamed: boolean; email: string; phones: string[] }
    | { kind: "post"; notNamed: boolean };

/** `notNamed`: the recipient signed, but not for this patient — the parent signs again, and the
 *  form names every child. */
export function consentNeed(recipient: Recipient, required: boolean, patientId?: string): ConsentNeed {
    if (!required) return { kind: "none" };
    const { digital, post } = consentStates(recipient, patientId);
    const notNamed = consentMissesPatient(recipient, patientId);
    if (digital === "postOnly") return post === "signed" ? { kind: "none" } : { kind: "post", notNamed };
    if (digital === "signed") return { kind: "none" };
    return {
        kind: "digital",
        requested: digital === "requested",
        postSigned: post === "signed",
        notNamed,
        email: recipient.email?.trim() ?? "",
        phones: (recipient.tel ?? []).map((p) => String(p ?? "").trim()).filter(Boolean),
    };
}

/** How the invoice could reach the recipient with the contact data on file. */
export function deliveryGaps(recipient: Recipient): { email: boolean; address: boolean } {
    return {
        email: !recipient.email?.trim(),
        address: !(recipient.street?.trim() && recipient.plz?.trim() && recipient.city?.trim()),
    };
}
